import * as React from "react"

import { cn } from "../../cn"
import { getSwatchRingColors } from "../../tokens/swatchContrast"

/**
 * A block of a user-chosen color — a tag dot, a palette square, a series key.
 *
 * The fill renders exactly as chosen (it is the item's identity), so it can land
 * at any lightness, including one that matches the surface behind it. The ring
 * carries the visibility instead: same hue, moved in lightness only as far as
 * 3:1 against the current theme's surface requires. On a fill that already
 * clears 3:1 the ring resolves to the fill itself and so reads as no ring at all.
 *
 * Both theme values are emitted as custom properties and selected in CSS, so the
 * swatch needs no theme hook and stays correct through SSR.
 */

/** Ring utilities for callers that render their own element (buttons, inputs). */
export const colorSwatchRingClassName =
  "ring-1 ring-[var(--swatch-ring)] dark:ring-[var(--swatch-ring-dark)]"

/** Fill + per-theme ring custom properties for `colorSwatchRingClassName`. */
export function colorSwatchStyle(color: string): React.CSSProperties {
  const { light, dark } = getSwatchRingColors(color)
  return {
    backgroundColor: color,
    "--swatch-ring": light,
    "--swatch-ring-dark": dark,
  } as React.CSSProperties
}

export interface ColorSwatchProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "color"
> {
  /** Any CSS color; hex values get a contrast-corrected ring. */
  color: string
}

const ColorSwatch = React.forwardRef<HTMLSpanElement, ColorSwatchProps>(
  ({ color, className, style, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "size-2 shrink-0 rounded-full",
          colorSwatchRingClassName,
          className,
        )}
        style={{ ...colorSwatchStyle(color), ...style }}
        {...props}
      />
    )
  },
)

ColorSwatch.displayName = "ColorSwatch"

export { ColorSwatch }
